import { useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, Trash2, Play, Pause } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 45;

/**
 * Inspelning av röstbeskrivning via MediaRecorder API.
 * Props:
 *   value: existerande audio_url (om inspelad tidigare)
 *   onChange: (url | null) => void
 */
export default function VoiceNoteRecorder({ value, onChange }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [playing, setPlaying] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const start = async () => {
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setUploading(true);
        try {
          const file = new File([blob], `roestbeskrivning-${Date.now()}.webm`, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", file);
          const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data?.url) onChange(r.data.url);
        } catch (e) {
          setErr("Uppladdning misslyckades");
        } finally {
          setUploading(false);
        }
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(e => {
          if (e + 1 >= MAX_SECONDS) {
            stopInternal();
            return MAX_SECONDS;
          }
          return e + 1;
        });
      }, 1000);
    } catch (e) {
      setErr("Mikrofonen är inte tillgänglig eller tillåten");
    }
  };

  const stopInternal = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const mr = mediaRecRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  const remove = () => {
    onChange(null);
    setPlaying(false);
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play(); setPlaying(true); }
  };

  const audioUrl = value ? (value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : null;

  return (
    <div data-testid="voice-note-recorder" className="bg-bone-alt/40 border border-warm-border rounded-sm p-3">
      <p className="text-[11px] font-body font-semibold text-bark/60 uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5">
        <Mic size={11} /> Röstbeskrivning (valfritt, max {MAX_SECONDS}s)
      </p>
      {!value && !recording && (
        <button
          type="button"
          data-testid="voice-record-start"
          onClick={start}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-parchment bg-falu hover:bg-falu-hover disabled:opacity-50 px-3 py-2 rounded-sm transition-colors"
        >
          <Mic size={13} /> {uploading ? "Laddar upp..." : "Spela in"}
        </button>
      )}
      {recording && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="voice-record-stop"
            onClick={stopInternal}
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-parchment bg-ink hover:bg-ink/85 px-3 py-2 rounded-sm animate-pulse"
          >
            <Square size={13} /> Stoppa
          </button>
          <span className="text-xs text-bark/70 font-mono">{String(elapsed).padStart(2, "0")}s / {MAX_SECONDS}s</span>
        </div>
      )}
      {value && !recording && (
        <div className="flex items-center gap-2">
          <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} />
          <button
            type="button"
            data-testid="voice-play"
            onClick={togglePlay}
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-parchment bg-pine hover:bg-pine-hover px-3 py-2 rounded-sm"
          >
            {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pausa" : "Lyssna"}
          </button>
          <button
            type="button"
            data-testid="voice-remove"
            onClick={remove}
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-falu hover:text-falu-hover bg-parchment border border-falu/30 px-2 py-2 rounded-sm"
            aria-label="Ta bort röstbeskrivning"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
      {err && <p className="text-[11px] text-falu mt-2">{err}</p>}
    </div>
  );
}
